import './LoginPage.scss';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Lock, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../i18n';
import { useToast } from '../../components/Toast';
import Input from '../../components/Input';
import Button from '../../components/Button';
import LangSwitch from '../../components/LangSwitch';
import Logo from '../../components/Logo';
import type { AuthChannel } from '../../services/auth';

function detectChannel(value: string): AuthChannel {
  const v = value.trim();
  if (v.includes('@')) return 'email';
  if (/^[+\d][\d\s()-]+$/.test(v)) return 'phone';
  return 'phone';
}

export default function LoginPage() {
  const { user, loading, loginPassword } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const toast = useToast();
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const effective = useMemo<AuthChannel>(() => detectChannel(contact), [contact]);
  const channelIcon = useMemo(() => {
    if (effective === 'email') return <Mail size={18} />;
    return <Phone size={18} />;
  }, [effective]);

  if (loading) {
    return (
      <div className="login-page__loading" role="status" aria-live="polite">
        <span className="login-page__spinner" aria-hidden="true" />
      </div>
    );
  }
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from || '/cabinet'} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!contact.trim() || !password) return;
    setError('');
    setSubmitting(true);
    try {
      await loginPassword(contact.trim(), password, effective);
      toast.success(t('login.success'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('login.invalid');
      setError(message || t('login.invalid'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__inner">
        <Link to="/" className="login-page__brand" aria-label={t('brand.name')}>
          <Logo variant="white-mark-text" size={56} />
        </Link>
        <div className="login-page__card">
          <div className="login-page__lang">
            <LangSwitch />
          </div>
          <h1 className="login-page__title">{t('login.title')}</h1>
          <p className="login-page__subtitle">{t('login.password_hint')}</p>
          <hr className="login-page__divider" />
          {error ? (
            <div className="login-page__error" role="alert">{error}</div>
          ) : null}
          <form onSubmit={onSubmit} className="login-page__form">
            <Input
              label={t('login.contact')}
              placeholder={t('login.contact_placeholder')}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              iconLeft={channelIcon}
              autoFocus
              required
              autoComplete="username"
            />
            <Input
              label={t('login.password')}
              type="password"
              placeholder={t('login.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              iconLeft={<Lock size={18} />}
              required
              autoComplete="current-password"
            />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
              {t('login.submit')}
            </Button>
          </form>
        </div>
        <div className="login-page__footer">
          <Link to="/" className="login-page__back">{t('login.back_home')}</Link>
        </div>
      </div>
    </div>
  );
}
